import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { createUseDataloader } from "@-/dev.improve.haniverse.lib.dataloader";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { useCallback } from "react";
import {
  getMorphemeDetailsTileSlices,
  type MorphemeDetailsTileSliceLookup,
} from "#get-morpheme-details-tile-slices";
// One DataLoader per (contentDb, staticDb) pair, shared across every consumer
// of this hook. Per-tick `load(lookup)` calls coalesce into one
// `getMorphemeDetailsTileSlices` batch. Bounded batches (`maxBatchSize=64`)
// keep the underlying `WHERE grapheme IN (…)` queries well under D1's parameter
// limit — a whole-page lookup would otherwise blow past
// `SQLITE_MAX_VARIABLE_NUMBER` on the web binding.
//
// Top-level `useLoader`: reads both DBs via context and closes over the pair, so
// the batch key is just the lookup — the static DB no longer has to be smuggled
// through each `.load()` entry.
function useLoader() {
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const batchLoad = useCallback(
    async (
      lookups: readonly MorphemeDetailsTileSliceLookup[],
    ): Promise<readonly (MorphemeDetailsTileSlice | null)[]> =>
      getMorphemeDetailsTileSlices(contentDb, staticDb, lookups),
    [contentDb, staticDb],
  );
  return { batchLoad, cacheIdentity: [contentDb, staticDb] as const };
}

const useMorphemeDetailsTileSliceLoader = createUseDataloader({ useLoader });

function lookupQueryKeyPart(
  lookup: MorphemeDetailsTileSliceLookup,
): readonly ["morpheme", string] | readonly ["id", number] {
  return "morpheme" in lookup ? ["morpheme", lookup.morpheme] : ["id", lookup.id];
}

export function useGetMorphemeDetailsTileSliceQueryKey() {
  const loader = useMorphemeDetailsTileSliceLoader();

  return (lookup: MorphemeDetailsTileSliceLookup) =>
    ["morphemeDetailsTileSlice", ...lookupQueryKeyPart(lookup), loader] as const;
}

/**
 * Hook returning a `queryOptions` factory for per-lookup morpheme details
 * button slices. Each lookup is its own Query cache entry; consumers calling
 * `useQuery(queryOptions(lookup))` (or `useQueries` over many lookups) in the
 * same tick coalesce into one batched DB query via DataLoader.
 *
 * Both DBs are immutable for the app's lifetime, so the query key does not
 * include a change-version.
 */
export const [useMorphemeDetailsTileSliceQueryOptions, morphemeDetailsTileSliceQueryFn] =
  createUseQueryOptions(useGetMorphemeDetailsTileSliceQueryKey, ({ queryKey }) => {
    const lookup: MorphemeDetailsTileSliceLookup =
      queryKey[1] === "morpheme" ? { morpheme: queryKey[2] } : { id: queryKey[2] };
    const loader = queryKey[3];
    return loader.load(lookup);
  });
